import React, { useState, useEffect, useRef } from 'react';
import { httpsCallable } from 'firebase/functions';
import { motion, AnimatePresence } from 'framer-motion';
import { useAppContext } from '../AppContext';
import Celebration from './Celebration';
import { useTranslation } from 'react-i18next';

// A more visually engaging orb/crystal component
const HarmonicOrb = ({ state, onMouseDown, onMouseUp }) => {
    const { LucideIcons } = useAppContext();
    const { t } = useTranslation();
    const stateConfig = {
        IDLE: {
            scale: 1,
            shadow: '0 0 20px rgba(128, 90, 213, 0.5)',
            color: '#a855f7', // purple-500
            icon: <LucideIcons.Fingerprint size={64} className="text-white/50" />,
            prompt: t('echoChamber.holdToAttune')
        },
        CHARGING: {
            scale: 1.1,
            shadow: '0 0 40px rgba(168, 85, 247, 0.9)',
            color: '#d8b4fe', // purple-300
            icon: <LucideIcons.Loader size={64} className="text-white/80 animate-spin" />,
            prompt: t('echoChamber.attuning')
        },
        REVEALING: {
            scale: 0.9,
            shadow: '0 0 60px rgba(255, 255, 255, 1)',
            color: '#ffffff',
            icon: <LucideIcons.Sparkles size={64} className="text-yellow-300" />,
            prompt: t('echoChamber.resonance')
        }
    };

    const current = stateConfig[state] || stateConfig.IDLE;

    return (
        <div className="flex flex-col items-center justify-center gap-6">
            <motion.div
                className="w-48 h-48 rounded-full flex items-center justify-center cursor-pointer"
                style={{
                    background: `radial-gradient(circle, ${current.color} 0%, rgba(128, 90, 213, 0.3) 70%)`,
                    border: `2px solid ${current.color}`,
                }}
                animate={{
                    scale: current.scale,
                    boxShadow: current.shadow,
                }}
                transition={{ type: 'spring', stiffness: 200, damping: 15 }}
                onMouseDown={onMouseDown}
                onMouseUp={onMouseUp}
                onTouchStart={onMouseDown}
                onTouchEnd={onMouseUp}
            >
                {current.icon}
            </motion.div>
            <p className="text-lg text-gray-300 h-6">{current.prompt}</p>
        </div>
    );
};

function EchoChamber({ onClose }) {
    const { appFunctions, setMessage, LucideIcons } = useAppContext();
    const { t } = useTranslation();
    const [chamberState, setChamberState] = useState('IDLE'); // IDLE, CHARGING, REVEALING, REVEALED, ERROR
    const [reward, setReward] = useState(null);
    const [error, setError] = useState('');
    const [progress, setProgress] = useState(0);
    const [showCelebration, setShowCelebration] = useState(false);

    const holdTimeout = useRef(null);
    const progressInterval = useRef(null);
    const CHARGE_TIME = 1500; // ms

    const startCharging = () => {
        if (chamberState !== 'IDLE') return;
        setChamberState('CHARGING');
        if (navigator.vibrate) navigator.vibrate(50);

        progressInterval.current = setInterval(() => {
            setProgress(p => Math.min(p + (100 / (CHARGE_TIME / 100)), 100));
        }, 100);

        holdTimeout.current = setTimeout(openChamber, CHARGE_TIME);
    };

    const cancelCharging = () => {
        if (chamberState !== 'CHARGING') return;
        setChamberState('IDLE');
        setProgress(0);
        clearTimeout(holdTimeout.current);
        clearInterval(progressInterval.current);
    };

    const openChamber = async () => {
        clearInterval(progressInterval.current);
        setChamberState('REVEALING');
        if (navigator.vibrate) navigator.vibrate([100, 30, 100]);

        const openEchoChamberCallable = httpsCallable(appFunctions, 'openEchoChamber');
        try {
            const result = await openEchoChamberCallable();
            setReward(result.data);
            setShowCelebration(true);
            setTimeout(() => setChamberState('REVEALED'), 500);
        } catch (err) {
            console.error("Echo Chamber opening failed:", err);
            setError(err.message || t('echoChamber.unstableConnection'));
            setChamberState('ERROR');
        }
    };

    const renderRewardContent = () => {
        if (!reward) return null;

        const rewardsConfig = {
            ECHO_TROVE_SMALL: {
                title: t('echoChamber.rewards.smallTrove.title'),
                icon: <LucideIcons.Flame size={48} className="text-yellow-400" />,
                description: t('echoChamber.rewards.smallTrove.description', { amount: reward.data.amount }),
                color: "yellow"
            },
            ECHO_TROVE_MEDIUM: {
                title: t('echoChamber.rewards.mediumTrove.title'),
                icon: <LucideIcons.Flame size={48} className="text-yellow-300" />,
                description: t('echoChamber.rewards.mediumTrove.description', { amount: reward.data.amount }),
                color: "yellow"
            },
            ECHO_TROVE_LARGE: {
                title: t('echoChamber.rewards.largeTrove.title'),
                icon: <LucideIcons.Flame size={48} className="text-amber-400" />,
                description: t('echoChamber.rewards.largeTrove.description', { amount: reward.data.amount }),
                color: "amber"
            },
            ECHO_JACKPOT: {
                title: t('echoChamber.rewards.echoJackpot.title'),
                icon: <LucideIcons.Trophy size={48} className="text-amber-300" />,
                description: t('echoChamber.rewards.echoJackpot.description', { amount: reward.data.amount }),
                color: "amber"
            },
            CRYSTAL_BALL_PROMPT: {
                title: t('echoChamber.rewards.crystalBallPrompt.title'),
                icon: <LucideIcons.Lightbulb size={48} className="text-purple-400" />,
                description: t('echoChamber.rewards.crystalBallPrompt.description', { prompt: reward.data.prompt }),
                color: "purple"
            },
            LOST_WHISPER: {
                title: t('echoChamber.rewards.lostWhisper.title'),
                icon: <LucideIcons.Feather size={48} className="text-sky-400" />,
                description: t('echoChamber.rewards.lostWhisper.description', { content: reward.data.content }),
                color: "sky"
            },
            REPUTATION_BOOST: {
                title: t('echoChamber.rewards.reputationBoost.title'),
                icon: <LucideIcons.ShieldCheck size={48} className="text-green-400" />,
                description: t('echoChamber.rewards.reputationBoost.description', { amount: reward.data.amount }),
                color: "green"
            },
            HARMONY_PRO_CREDIT: {
                title: t('echoChamber.rewards.harmonyProCredit.title'),
                icon: <LucideIcons.Crown size={48} className="text-teal-400" />,
                description: t('echoChamber.rewards.harmonyProCredit.description'),
                color: "teal"
            },
            DEFAULT: {
                title: t('echoChamber.rewards.default.title'),
                icon: <LucideIcons.HelpCircle size={48} />,
                description: t('echoChamber.rewards.default.description'),
                color: "gray"
            }
        };

        const config = rewardsConfig[reward.type] || rewardsConfig.DEFAULT;

        return (
            <div className="text-center">
                <div className={`mx-auto w-24 h-24 rounded-full flex items-center justify-center bg-${config.color}-500/20 mb-4 border-2 border-${config.color}-500`}>
                    {config.icon}
                </div>
                <h3 className={`text-3xl font-bold text-${config.color}-300 mb-2`}>{config.title}</h3>
                <p className="text-gray-300">{config.description}</p>
            </div>
        );
    };

    return (
        <motion.div
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onMouseUp={cancelCharging} // Cancel if mouse moves out and up
            onTouchEnd={cancelCharging}
        >
            {showCelebration && <Celebration onComplete={() => setShowCelebration(false)} />}
            <div className="w-full max-w-md text-center relative">
                <AnimatePresence>
                    {chamberState !== 'REVEALED' && chamberState !== 'ERROR' && (
                        <motion.div
                            key="chamber"
                            initial={{ scale: 0.8, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.8, opacity: 0 }}
                            className="bg-gray-900/50 backdrop-blur-lg p-8 rounded-2xl shadow-2xl border border-white/10"
                        >
                            <button onClick={onClose} className="absolute top-4 right-4 p-2 rounded-full hover:bg-white/20 transition-colors" aria-label={t('echoChamber.close')}>
                                <LucideIcons.X size={24} className="text-white/70" />
                            </button>
                            <h2 className="text-2xl font-bold text-white mb-2">{t('echoChamber.title')}</h2>
                            <p className="text-gray-400 mb-8">{t('echoChamber.description')}</p>
                            <HarmonicOrb state={chamberState} onMouseDown={startCharging} onMouseUp={cancelCharging} />
                            <div className="w-full bg-gray-700 rounded-full h-2.5 mt-8">
                                <motion.div
                                    className="bg-purple-500 h-2.5 rounded-full"
                                    style={{ width: `${progress}%` }}
                                    initial={{ width: 0 }}
                                    animate={{ width: `${progress}%` }}
                                />
                            </div>
                        </motion.div>
                    )}

                    {chamberState === 'REVEALED' && (
                        <motion.div
                            key="reward"
                            initial={{ scale: 0.8, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            className="bg-gray-900/80 backdrop-blur-md p-8 rounded-lg shadow-glow border border-purple-500/50"
                        >
                            {renderRewardContent()}
                            <button onClick={onClose} className="mt-8 w-full py-3 bg-blue-600 text-white font-bold rounded-full hover:bg-blue-700 transition-transform hover:scale-105">
                                {t('echoChamber.continue')}
                            </button>
                        </motion.div>
                    )}

                    {chamberState === 'ERROR' && (
                        <motion.div
                            key="error"
                            initial={{ scale: 0.8, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            className="bg-red-900/50 backdrop-blur-md p-8 rounded-lg shadow-lg border border-red-500/50"
                        >
                            <div className="mx-auto w-20 h-20 rounded-full flex items-center justify-center bg-red-500/20 mb-4">
                                <LucideIcons.AlertTriangle size={40} className="text-red-400" />
                            </div>
                            <h3 className="text-2xl font-bold text-red-300 mb-2">{t('echoChamber.resonanceFailed')}</h3>
                            <p className="text-gray-300">{error}</p>
                            <button onClick={onClose} className="mt-8 w-full py-3 bg-gray-600 text-white font-bold rounded-full hover:bg-gray-700 transition-transform hover:scale-105">
                                {t('echoChamber.close')}
                            </button>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </motion.div>
    );
}

export default EchoChamber;
